import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Button,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Description as DocIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  Update as UpdateIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Search as SearchIcon,
  ArrowForward as ArrowIcon,
  Star as StarIcon,
  NewReleases as NewIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material';
import logo from '../assets/favicon.png';

const Docs = () => {
  const [selectedSection, setSelectedSection] = useState(null);

  const documentationSections = [
    {
      title: "Getting Started",
      icon: <DocIcon sx={{ color: '#00693C' }} />,
      items: [
        { name: "System Requirements", isNew: true, path: "/docs/requirements" },
        { name: "Installation Guide", isPopular: true, path: "/docs/installation" },
        { name: "Quick Start Tutorial", isNew: true, path: "/docs/quickstart" },
        { name: "Basic Configuration", path: "/docs/basic-config" },
        { name: "Environment Setup", path: "/docs/env-setup" },
        { name: "First-time User Guide", isPopular: true, path: "/docs/user-guide" },
        { name: "Best Practices", path: "/docs/best-practices" },
        { name: "Common Pitfalls", path: "/docs/pitfalls" }
      ],
      description: "Everything you need to begin your journey with our platform."
    },
    {
      title: "AI Features",
      icon: <SecurityIcon sx={{ color: '#00693C' }} />,
      items: [
        { name: "AI Diagnosis Setup", isPopular: true, path: "/docs/ai-setup" },
        { name: "Machine Learning Models", isNew: true, path: "/docs/ml-models" },
        { name: "Training Data Requirements", path: "/docs/training-data" },
        { name: "Performance Optimization", isPopular: true, path: "/docs/performance" },
        { name: "Model Customization", isNew: true, path: "/docs/customization" },
        { name: "AI Integration Guide", path: "/docs/ai-integration" },
        { name: "Real-time Processing", path: "/docs/real-time" },
        { name: "Advanced Analytics", path: "/docs/analytics" }
      ],
      description: "Leverage our advanced AI capabilities for enhanced performance."
    },
    {
      title: "API Reference",
      icon: <CodeIcon sx={{ color: '#00693C' }} />,
      items: [
        { name: "REST API Endpoints", isPopular: true, path: "/docs/api-endpoints" },
        { name: "Authentication Methods", path: "/docs/auth" },
        { name: "Rate Limits & Quotas", path: "/docs/rate-limits" },
        { name: "Error Handling", path: "/docs/errors" },
        { name: "WebSocket API", isNew: true, path: "/docs/websocket" },
        { name: "API Versioning", path: "/docs/versioning" },
        { name: "Response Formats", path: "/docs/responses" },
        { name: "SDK Documentation", path: "/docs/sdk" }
      ],
      description: "Complete API documentation for developers and integrators."
    },
    {
      title: "Updates & Maintenance",
      icon: <UpdateIcon sx={{ color: '#00693C' }} />,
      items: [
        { name: "Version History", path: "/docs/versions" },
        { name: "Update Procedures", isPopular: true, path: "/docs/updates" },
        { name: "Backup Guidelines", path: "/docs/backup" },
        { name: "Maintenance Schedule", path: "/docs/maintenance" },
        { name: "Migration Guide", isNew: true, path: "/docs/migration" },
        { name: "Release Notes", path: "/docs/releases" },
        { name: "Deprecation Policy", path: "/docs/deprecation" },
        { name: "Rollback Procedures", path: "/docs/rollback" }
      ],
      description: "Stay up to date with the latest updates and maintenance procedures."
    },
    {
      title: "Configuration",
      icon: <SettingsIcon sx={{ color: '#00693C' }} />,
      items: [
        { name: "System Settings", isPopular: true, path: "/docs/settings" },
        { name: "User Management", path: "/docs/users" },
        { name: "Security Options", path: "/docs/security" },
        { name: "Integration Setup", path: "/docs/integrations" },
        { name: "Custom Workflows", isNew: true, path: "/docs/workflows" },
        { name: "Access Control", path: "/docs/access" },
        { name: "Network Configuration", path: "/docs/network" },
        { name: "Advanced Options", path: "/docs/advanced" }
      ],
      description: "Detailed configuration guides for system administrators."
    },
    {
      title: "Troubleshooting",
      icon: <HelpIcon sx={{ color: '#00693C' }} />,
      items: [
        { name: "Common Issues", isPopular: true, path: "/docs/issues" },
        { name: "Diagnostic Tools", path: "/docs/diagnostics" },
        { name: "Error Messages", path: "/docs/errors" },
        { name: "Support Contact", path: "/docs/support" },
        { name: "Debug Mode", isNew: true, path: "/docs/debug" },
        { name: "System Logs", path: "/docs/logs" },
        { name: "Performance Issues", path: "/docs/performance-issues" },
        { name: "FAQ", path: "/docs/faq" }
      ],
      description: "Solutions for common problems and troubleshooting guides."
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#1a1a1a' }}>
      {/* Enhanced Header */}
      <Box sx={{ 
        bgcolor: '#000000', 
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            py: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src={logo} 
                alt="Logo" 
                style={{ 
                  height: 30,
                  filter: 'brightness(0) invert(1)',
                  cursor: 'pointer'
                }} 
                onClick={() => window.location.href = '/dashboard'}
              />
              <Typography variant="h6" sx={{ ml: 2, color: 'white' }}>
                Documentation
              </Typography>
            </Box>
            
            {/* Search Bar */}
            <TextField
              placeholder="Search documentation..."
              variant="outlined"
              size="small"
              sx={{
                width: '300px',
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  border: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                  '& fieldset': {
                    border: 'none'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Featured Section */}
      <Box sx={{ bgcolor: '#000000', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ mb: 3, fontWeight: 600 }}>
            Technical Documentation
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4 }}>
            Comprehensive guides and references for developers and system administrators
          </Typography>
          <Grid container spacing={2}>
            <Grid item>
              <Button 
                variant="contained" 
                sx={{ 
                  bgcolor: '#00693C',
                  '&:hover': { bgcolor: '#004d2d' },
                  borderRadius: 0
                }}
                endIcon={<ArrowIcon />}
              >
                Get Started
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="outlined" 
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  '&:hover': { borderColor: '#00693C', color: '#00693C' },
                  borderRadius: 0
                }}
              >
                View Examples
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {documentationSections.map((section, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper 
                sx={{ 
                  p: 3,
                  height: '100%',
                  bgcolor: '#ffffff',
                  borderRadius: 0,
                  border: '1px solid rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {section.icon}
                  <Typography variant="h6" sx={{ ml: 1, color: '#000' }}>
                    {section.title}
                  </Typography>
                </Box>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(0,0,0,0.6)', 
                    mb: 2,
                    borderLeft: '2px solid #00693C',
                    pl: 2
                  }}
                >
                  {section.description}
                </Typography>

                <List>
                  {section.items.map((item, itemIndex) => (
                    <React.Fragment key={itemIndex}>
                      <ListItem 
                        sx={{ 
                          py: 1.5,
                          px: 2,
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'rgba(0, 105, 60, 0.05)',
                          }
                        }}
                      >
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {item.name}
                              {item.isNew && (
                                <Chip 
                                  label="NEW" 
                                  size="small" 
                                  icon={<NewIcon sx={{ fontSize: 14 }} />}
                                  sx={{ 
                                    ml: 1, 
                                    bgcolor: '#00693C', 
                                    color: 'white',
                                    height: 20,
                                    borderRadius: 0
                                  }} 
                                />
                              )}
                              {item.isPopular && (
                                <Chip 
                                  label="POPULAR" 
                                  size="small" 
                                  icon={<StarIcon sx={{ fontSize: 14 }} />}
                                  sx={{ 
                                    ml: 1, 
                                    bgcolor: '#ff9800', 
                                    color: 'white',
                                    height: 20,
                                    borderRadius: 0
                                  }} 
                                />
                              )}
                            </Box>
                          }
                          sx={{ 
                            '& .MuiListItemText-primary': {
                              color: '#333',
                              fontSize: '0.95rem'
                            }
                          }}
                        />
                        <ArrowIcon 
                          sx={{ 
                            color: 'rgba(0,0,0,0.3)',
                            fontSize: 18
                          }} 
                        />
                      </ListItem>
                      {itemIndex < section.items.length - 1 && (
                        <Divider sx={{ opacity: 0.1 }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Docs; 